import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  createUserWithEmailAndPassword,
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signInWithPopup,
  updateProfile,
  type User,
} from 'firebase/auth'
import { toast } from 'sonner'
import { auth, googleProvider, appleProvider, errorParam, track } from './app'
import { authErrorMessage } from './auth-errors'
import { ensureRemoteConfig } from './remote-config'

export type AuthStatus = 'loading' | 'signedOut' | 'signedIn'

/**
 * Progress of an email-link sign-in when the app was opened from the link:
 * `completing` while we exchange it, `needsEmail` when it was opened on a
 * browser that doesn't remember which address the link was sent to.
 */
export type EmailLinkStatus = 'none' | 'completing' | 'needsEmail'

/** Where we remember the address a sign-in link was sent to (same as Firebase's docs). */
const EMAIL_FOR_SIGN_IN_KEY = 'emailForSignIn'
const EMAIL_LINK_PARAMS = ['apiKey', 'oobCode', 'mode', 'continueUrl', 'lang', 'tenantId']

interface AuthContextValue {
  status: AuthStatus
  user: User | null
  signInWithGoogle: () => Promise<void>
  signInWithApple: () => Promise<void>
  /** Rejects with the raw Firebase error — render it with `authErrorMessage`. */
  signInWithEmail: (email: string, password: string) => Promise<void>
  signUpWithEmail: (email: string, password: string, name?: string) => Promise<void>
  sendPasswordReset: (email: string) => Promise<void>
  /** Emails a passwordless sign-in link that brings the person back to this page. */
  sendSignInLink: (email: string) => Promise<void>
  emailLinkStatus: EmailLinkStatus
  /** Finishes a `needsEmail` link sign-in. Rejects with the raw Firebase error. */
  completeEmailLinkSignIn: (email: string) => Promise<void>
  cancelEmailLinkSignIn: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ status: AuthStatus; user: User | null }>({
    status: 'loading',
    user: null,
  })
  const [emailLinkStatus, setEmailLinkStatus] = useState<EmailLinkStatus>(() =>
    isSignInWithEmailLink(auth, window.location.href) ? 'completing' : 'none',
  )
  // A link's code is single-use, so StrictMode's double effect must not redeem it twice.
  const emailLinkHandled = useRef(false)

  useEffect(() => {
    // Make sure Remote Config (server URL, Stripe tables…) is loading early.
    void ensureRemoteConfig()
    return onAuthStateChanged(auth, (user) => {
      setState({ status: user ? 'signedIn' : 'signedOut', user })
    })
  }, [])

  const completeEmailLinkSignIn = useCallback(async (email: string) => {
    track('login_email_link')
    try {
      await signInWithEmailLink(auth, email.trim(), window.location.href)
    } catch (error) {
      track('login_error', { error: errorParam(error) })
      throw error
    }
    track('login_email_success')
    track('login_success')
    localStorageRemove(EMAIL_FOR_SIGN_IN_KEY)
    stripEmailLinkParams()
    setEmailLinkStatus('none')
  }, [])

  const cancelEmailLinkSignIn = useCallback(() => {
    stripEmailLinkParams()
    setEmailLinkStatus('none')
  }, [])

  useEffect(() => {
    if (state.status === 'loading' || emailLinkStatus !== 'completing') return
    if (emailLinkHandled.current) return
    emailLinkHandled.current = true

    if (state.status === 'signedIn') {
      stripEmailLinkParams()
      setEmailLinkStatus('none')
      toast.info("You're already signed in")
      return
    }

    const email = localStorageGet(EMAIL_FOR_SIGN_IN_KEY)
    if (!email) {
      setEmailLinkStatus('needsEmail')
      return
    }
    completeEmailLinkSignIn(email).catch((error: unknown) => {
      console.error('Email link sign-in failed', error)
      toast.error('Sign-in failed', { description: authErrorMessage(error) })
      cancelEmailLinkSignIn()
    })
  }, [state.status, emailLinkStatus, completeEmailLinkSignIn, cancelEmailLinkSignIn])

  const signInWithGoogle = useCallback(async () => {
    track('login_google')
    try {
      await signInWithPopup(auth, googleProvider)
      track('google_login_success')
      track('login_success')
    } catch (error) {
      handleSignInError(error)
    }
  }, [])

  const signInWithApple = useCallback(async () => {
    track('login_apple')
    try {
      await signInWithPopup(auth, appleProvider)
      track('apple_login_success')
      track('login_success')
    } catch (error) {
      handleSignInError(error)
    }
  }, [])

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    track('login_password')
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password)
    } catch (error) {
      track('login_password_error', { error: errorParam(error) })
      throw error
    }
    track('login_password_success')
    track('login_success')
  }, [])

  const signUpWithEmail = useCallback(
    async (email: string, password: string, name?: string) => {
      const { user } = await createUserWithEmailAndPassword(auth, email.trim(), password).catch(
        (error: unknown) => {
          track('login_password_error', { error: errorParam(error) })
          throw error
        },
      )
      track('login_password_created')
      track('login_success')

      const displayName = name?.trim()
      if (displayName) {
        await updateProfile(user, { displayName })
        // `updateProfile` mutates the same User instance that onAuthStateChanged
        // already handed us, so nudge React into re-rendering with the new name.
        setState((prev) => ({ ...prev }))
      }

      try {
        await sendEmailVerification(user)
      } catch (error) {
        // Best-effort: the account exists and is usable either way.
        console.error('Could not send the verification email', error)
      }
    },
    [],
  )

  const sendPasswordReset = useCallback(async (email: string) => {
    await sendPasswordResetEmail(auth, email.trim())
    track('login_password_reset_sent')
  }, [])

  const sendSignInLink = useCallback(async (email: string) => {
    track('login_email_start')
    const address = email.trim()
    try {
      await sendSignInLinkToEmail(auth, address, {
        // Come back to the page the person is on, so deep links survive sign-in.
        url: window.location.origin + window.location.pathname,
        handleCodeInApp: true,
      })
    } catch (error) {
      track('login_email_start_error', { error: errorParam(error) })
      throw error
    }
    track('login_email_start_success')
    localStorageSet(EMAIL_FOR_SIGN_IN_KEY, address)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        ...state,
        signInWithGoogle,
        signInWithApple,
        signInWithEmail,
        signUpWithEmail,
        sendPasswordReset,
        sendSignInLink,
        emailLinkStatus,
        completeEmailLinkSignIn,
        cancelEmailLinkSignIn,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

function handleSignInError(error: unknown) {
  const code = (error as { code?: string }).code
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return
  track(code === 'auth/account-exists-with-different-credential' ? 'login_provider_mismatch' : 'login_error', {
    error: errorParam(error),
  })
  console.error('Sign-in failed', error)
  toast.error('Sign-in failed', { description: authErrorMessage(error) })
}

/** Drops the one-time link parameters so a reload doesn't try to redeem the code again. */
function stripEmailLinkParams() {
  const url = new URL(window.location.href)
  for (const param of EMAIL_LINK_PARAMS) url.searchParams.delete(param)
  window.history.replaceState(window.history.state, '', url)
}

function localStorageGet(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function localStorageSet(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Without storage the link still works; we'll just ask for the email again.
  }
}

function localStorageRemove(key: string) {
  try {
    localStorage.removeItem(key)
  } catch {
    // Ignore — see localStorageSet.
  }
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

/** The signed-in Firebase user. Only call under RequireAuth. */
export function useUser(): User {
  const { user } = useAuth()
  if (!user) throw new Error('useUser called while signed out')
  return user
}
